/** @jsx jsx */
import {
  Box,
  Button,
  Container,
  DottedList,
  DottedListItem,
  Flex,
  Heading,
  InformationFillIcon,
  Paragraph,
  Users2Icon,
} from "@eworkit/iris";
import React, { FC, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { DialogDisclosure, useDialogState } from "reakit/Dialog";
import { jsx } from "theme-ui";

import { LazyUserAvatar } from "@/components/avatars";
import { ResponsiveCard } from "@/components/ResponsiveCard";
import { UserContext } from "@/components/User";
import { fetchJobApplication } from "@/data/job-applications";
import { formatFullName } from "@/data/persons";
import { useCodelistTranslation } from "@/hooks/Codelist";
import { useToasts } from "@/views/components/Toast/useToasts";

import { ApplyExitModal } from "../_components/ApplyExitModal";
import { useApplyWizard } from "../_components/ApplyWizardContext";

interface ApplyChangeCandidateProps {}

export const ApplyChangeCandidate: FC<ApplyChangeCandidateProps> = () => {
  const { t } = useTranslation("session");
  const tCodelist = useCodelistTranslation();

  const { contextCompany } = useContext(UserContext);
  const { addToast } = useToasts();

  const { goToNextStep, profile, setProfile, setDraft, initialProfile, draft, designatedCandidate, resetSavingState } =
    useApplyWizard();

  const exitModalState = useDialogState({ animated: true });

  const handleSaved = useCallback(() => {
    setProfile(undefined);
    setDraft(undefined);
    resetSavingState();
  }, [setProfile, setDraft, resetSavingState]);

  const handleRemoved = useCallback(() => {
    setProfile(undefined);
    setDraft(undefined);
    resetSavingState();
  }, [setProfile, setDraft, resetSavingState]);

  const handleChangeCandidateClick = useCallback(() => {
    setProfile(undefined);
    resetSavingState();
  }, [setProfile, resetSavingState]);

  const handleContinueClick = useCallback(async () => {
    try {
      if (profile!.draftApplicationId) {
        const draft = await fetchJobApplication({
          jobApplicationId: profile!.draftApplicationId,
          companyId: contextCompany?.id,
        });

        setDraft(draft);
      }
      goToNextStep();
    } catch (error) {
      addToast({
        text: t("common:ERROR_MESSAGES.GENERAL"),
        type: "danger",
      });
    }
  }, [profile, setDraft, goToNextStep, contextCompany, addToast, t]);

  return (
    <Flex flexDirection="column">
      <Container variant="narrow">
        <Box px="sm" py={["sm", null, "md"]}>
          <Flex flexDirection="column" gap="md">
            <Flex flexDirection="column">
              {designatedCandidate ? (
                <React.Fragment>
                  <Heading size={["h-sm", null, "h-md"]}>{t("JOB_REQUESTS.APPLY.WIZARD.DESIGNATED_CANDIDATE")}</Heading>
                  <Paragraph color="n700">
                    {t("JOB_REQUESTS.APPLY.WIZARD.SELECT_OR_CHANGE_DESIGNATED_CANDIDATE_TO_APPLY", {
                      candidate: designatedCandidate,
                    })}
                  </Paragraph>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <Heading size={["h-sm", null, "h-md"]}>{t("JOB_REQUESTS.APPLY.WIZARD.CANDIDATE")}</Heading>
                  <Paragraph color="n700">{t("JOB_REQUESTS.APPLY.WIZARD.SELECT_CANDIDATE_OR_CHANGE")}</Paragraph>
                </React.Fragment>
              )}
            </Flex>
            <ResponsiveCard
              sx={{
                px: ["sm", "sm", "md", "md"],
                py: ["sm", "sm", "sm", "sm"],
                borderWidth: 1,
                borderRadius: "lg",
              }}
              onClick={handleContinueClick}>
              <Flex flexDirection={["column", null, "row"]} gap="xs">
                <Flex flexGrow={1} flexDirection={["column", null, "row"]} justifyContent="space-between" gap="md">
                  <Flex flexDirection={["column", null, "row"]} gap="xs">
                    <LazyUserAvatar user={profile!} />

                    <Flex flexDirection="column" gap="sm">
                      <Flex flexDirection="column">
                        <Paragraph bold>{formatFullName(profile)}</Paragraph>
                        <DottedList responsive>
                          {profile!.position?.name && (
                            <DottedListItem sx={{ maxWidth: "100%" }}>
                              <Paragraph color="n700" ellipsis>
                                {profile!.position.name}
                              </Paragraph>
                            </DottedListItem>
                          )}
                          <DottedListItem sx={{ maxWidth: "100%" }}>
                            <Paragraph color="n700" ellipsis>
                              {profile!.email}
                            </Paragraph>
                          </DottedListItem>
                        </DottedList>
                      </Flex>

                      {(Boolean(profile!.applicationCount) || (profile!.draftApplicationId && initialProfile)) && (
                        <Flex gap="xxs" alignItems="center">
                          <InformationFillIcon color="b500" size="sm" />
                          <Paragraph>
                            {profile!.draftApplicationId
                              ? t("JOB_REQUESTS.APPLY.WIZARD.DRAFT_INFO")
                              : profile!.applicationCount === 1
                              ? t("JOB_REQUESTS.APPLY.WIZARD.ONE_APPLICATION_INFO", {
                                  status: tCodelist(
                                    "job-request-application-statuses",
                                    profile!.firstApplicationStatus || "",
                                  ),
                                })
                              : t("JOB_REQUESTS.APPLY.WIZARD.MANY_APPLICATIONS_INFO", {
                                  count: profile!.applicationCount!,
                                })}
                          </Paragraph>
                        </Flex>
                      )}
                    </Flex>
                  </Flex>

                  <Box>
                    <Button>{t("JOB_REQUESTS.APPLY.WIZARD.CONTINUE")}</Button>
                  </Box>
                </Flex>
              </Flex>
            </ResponsiveCard>

            <Flex justifyContent="center" py="xxl">
              <Flex flexDirection="column" sx={{ maxWidth: 448 }} gap="md" alignItems="center">
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  borderColor="n500"
                  border="primary"
                  sx={{ borderRadius: "50%", width: 128, height: 128 }}>
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor="n500"
                    sx={{ borderRadius: "50%", width: 110, height: 110 }}>
                    <Users2Icon size="xl" color="n0" />
                  </Flex>
                </Flex>
                <Flex flexDirection="column" alignItems="center" gap="xs">
                  <Heading size={["h-sm", null, "h-md"]}>{t("JOB_REQUESTS.APPLY.WIZARD.CHANGE_CANDIDATE")}</Heading>
                  <Paragraph align="center" color="n700">
                    {t("JOB_REQUESTS.APPLY.WIZARD.CHANGE_CANDIDATE_INFO")}
                  </Paragraph>

                  {draft ? (
                    <React.Fragment>
                      <Button variant="secondary" as={DialogDisclosure} {...exitModalState}>
                        {t("JOB_REQUESTS.APPLY.WIZARD.CHANGE_CANDIDATE")}
                      </Button>
                      <ApplyExitModal dialogState={exitModalState} onSaved={handleSaved} onRemoved={handleRemoved} />
                    </React.Fragment>
                  ) : (
                    <Button variant="secondary" onClick={handleChangeCandidateClick}>
                      {t("JOB_REQUESTS.APPLY.WIZARD.CHANGE_CANDIDATE")}
                    </Button>
                  )}
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Box>
      </Container>
    </Flex>
  );
};
